import { DocFileViewModel } from '../../models/loan';
import { BuydownTypeEnum } from '../../models/mortgage.model';

/**
 * Main form model for Closing Disclosure Document Request
 * Maps to: LE.Lodasoft.Services.Disclosure.Models.ClosingDisclosureDocRequestModels.ClosingDisclosureDocRequestFormModel
 */
export interface ClosingDisclosureDocRequestFormModel {
  applicationId: number;
  showCorrespondentOnlyFields: boolean;

  brokerInformation: BrokerInformationSection;
  propertyInformation: PropertyInformationSection;
  loanInformation: LoanInformationSection;
  fees: FeesSection;

  sellerRealEstateAgent?: CdContactInfo;
  buyerRealEstateAgent?: CdContactInfo;
  noSellerRealEstateAgentUsed?: boolean;
  noRealEstateAgentUsed?: boolean;
  titleInformation: TitleInfo;
  escrowInformation: CdContactInfo;
  settlementInformation: CdContactInfo;

  /** Will Snap Docs be used? - maps to CX.ESIGN.SNAP.YN as Y/N */
  willSnapDocsBeUsed?: boolean;

  /** Is this a hybrid closing? - maps to CX.Hybrid.Closing as Yes/No */
  isHybridClosing?: boolean;

  /** Correspondent/Non-Del only fields */
  correspondentOnly: CorrespondentOnlySection;

  /**
   * Flow type for Correspondent channel: ICD (Initial Closing Disclosure) or DocOrder (Final Closing Documents)
   * Only applicable when showCorrespondentOnlyFields is true
   */
  flowType?: ClosingDisclosureFlowType;

  /** Additional comments to include in the generated PDF */
  additionalComments?: string;

  status?: string;
  isSubmitted?: boolean;
  submittedDate?: string;
  generatedDocument?: DocFileViewModel;
}

/**
 * Property information section
 * Maps to: PropertyInformationSection
 */
export interface PropertyInformationSection {
  streetAddress?: string;           // LS
  city?: string;                    // LS
  state?: string;                   // LS
  zip?: string;                     // LS

  mersMin?: string;                 // MERS MIN
  propertyType?: CdPropertyType;    // 1041

  mannerTitleHeldAs?: CdMannerTitleHeldAs; // 33
  /** @deprecated Use mannerTitleHeldAs. */
  titleHeldIn?: string;
  vesting?: string;                 // 1867

  willUsePoa?: boolean;             // Yes/No
  willCloseInTrust?: boolean;       // 1859, Yes/No
  nonBorrowerTitleHolder?: boolean; // Yes/No

  nonBorrowerTitleHolderName?: string;  // TR0101
  nonBorrowerTitleHolderPhone?: string;
  nonBorrowerTitleHolderEmail?: string;

  warehouse?: string;               // Warehouse field from Doc Order

  isLlc?: boolean;                  // Yes/No - LLC field for ICD and Doc Order
}

/**
 * Broker information section
 * Maps to: BrokerInformationSection
 */
export interface BrokerInformationSection {
  // Wholesale & Doc Order versions share these concepts
  // CD Request - Wholesale
  tpoCompanyNmls?: string;
  processorName?: string;
  processorEmail?: string;
  loanOfficerName?: string;
  loanOfficerEmail?: string;
  tpoCompanyName?: string;
  tpoCompanyPhone?: string;
  tpoCompanyFax?: string;
}

/**
 * Loan information section
 * Maps to: LoanInformationSection
 */
export interface LoanInformationSection {
  loanNumber?: string;              // Import from LS (Encompass loan #)
  programName?: string;             // 1401

  buydown?: boolean;                // 425
  buydownType?: BuydownTypeEnum;    // 4645
  buydownContributor?: CdBuydownContributorType; // CASASRN.X142
  changeBuydown?: boolean;          // UI-only gate for buydown sub-fields

  loanPurpose?: CdLoanPurpose;      // 19
  amortizationType?: CdAmortizationType; // 608

  appraisedValue?: number;          // 356
  noteRate?: number;                // 3

  baseLoanAmount?: number;          // 1109
  totalLoanAmount?: number;         // 2

  impoundsWaived?: boolean;         // Yes/No dropdown

  salesPrice?: number;              // 136

  closeOfEscrowDate?: string;       // 763 - ISO date string
  firstPaymentDate?: string;        // 682 - ISO date string

  // From LS
  ltv?: number;
  cltv?: number;
}

/**
 * Represents a user-defined dynamic fee with a custom name and amount
 * Maps to: DynamicFee
 */
export interface DynamicFee {
  uuid?: string;  // Client-side unique identifier for tracking
  name: string;
  amount: number | null;
}

/**
 * Fees section
 * Maps to: FeesSection
 */
export interface FeesSection {
  // Origination Charges
  applicationFee?: number;          // L229
  loanOriginationFee?: number;    // 454
  discountPointFee?: number;        // custom / mapped
  underwriterFee?: number;          // 367
  inHouseProcessingFee?: number;    // 1842 - Processing Fee - Staff
  thirdPartyProcessingFee?: number; // NEWHUD.X1151
  processorFee?: number;            // 1621

  surveyFee?: number;               // NEWHUD.X988

  appraisalFee?: number;            // 641

  creditReportFee?: number;         // 640

  floodCertFee?: number;            // NEWHUD.X400

  // Lender Credits
  interestRateCredit?: number;      // Import from LS (must be negative)

  // New fee fields from Encompass (read-only)
  borrowerPaidCompensation?: number;  // 439
  lenderPaidCompensation?: number;    // NEWHUD.X1142
  originationCredit?: number;         // NEWHUD.X1144
  txAttorneyFee?: number;             // 370

  /** Correspondent Only */
  fundingFee?: number;
  /** Correspondent Only */
  wireFee?: number;

  // Dynamic Fees - User-defined fees that can be added/removed at runtime
  dynamicOriginationFees?: DynamicFee[];
  dynamicServicesCanShop?: DynamicFee[];
  dynamicServicesCannotShop?: DynamicFee[];
  dynamicOtherFees?: DynamicFee[];
}

/**
 * Contact information for CD-related parties
 * Maps to: CdContactInfo
 */
export interface CdContactInfo {
  companyName?: string;             // VEND.X144
  companyLicenseId?: string;        // VEND.X937
  companyAddress?: string;          // VEND.X145
  city?: string;                    // VEND.X146
  state?: string;                   // VEND.X147
  zip?: string;                     // VEND.X148
  contactFullName?: string;         // VEND.X150
  contactLicenseId?: string;        // VEND.X929
  contactEmailAddress?: string;     // VEND.X152
  contactPhoneNumber?: string;      // VEND.X151
}

/**
 * Title information extending contact info
 * Maps to: TitleInfo
 */
export interface TitleInfo extends CdContactInfo {
  titleCaseNumber?: string;         // 187
}

/**
 * Fields that only appear in Correspondent/Non-Del version (ICD/Doc Order)
 * Not present in Wholesale version (CD Request)
 * Maps to: CorrespondentOnlySection
 */
export interface CorrespondentOnlySection {
  compensationPlan?: string;
  accountManagerEmail?: string;
  lockExpirationDate?: string;      // ISO date string
}

/**
 * Loan purpose types for CD Request
 * Maps to: CdLoanPurpose enum
 */
export enum CdLoanPurpose {
  ConstructionToPermanent = 'ConstructionToPermanent',
  ConstructionOnly = 'ConstructionOnly',
  Purchase = 'Purchase',
  NoCashOutRefinance = 'NoCashOutRefinance',
  CashOutRefinance = 'CashOutRefinance',
  Other = 'Other',
}

/**
 * Property types for CD Request
 * Maps to: CdPropertyType enum
 */
export enum CdPropertyType {
  Attached = 'Attached',
  Condominium = 'Condominium',
  Cooperative = 'Cooperative',
  Detached = 'Detached',
  DetachedCondo = 'DetachedCondo',
  HighRiseCondominium = 'HighRiseCondominium',
  ManufacturedHomeCondoPUDCoOp = 'ManufacturedHomeCondoPUDCoOp',
  ManufacturedHousing = 'ManufacturedHousing',
  MHHomeChoice = 'MHHomeChoice',
  MHAdvantage = 'MHAdvantage',
  MHSelect = 'MHSelect',
  PUD = 'PUD',
}

export enum CdMannerTitleHeldAs {
  SoleOwnership = 'SoleOwnership',
  LifeEstate = 'LifeEstate',
  TenancyInCommon = 'TenancyInCommon',
  JointTenancyWithRightOfSurvivorship = 'JointTenancyWithRightOfSurvivorship',
  TenancyByTheEntirety = 'TenancyByTheEntirety',
  AllAsJointTenants = 'AllAsJointTenants',
  AllAsTenantsInCommon = 'AllAsTenantsInCommon',
  AsCommunityProperty = 'AsCommunityProperty',
  AsHerSoleAndSeparateProperty = 'AsHerSoleAndSeparateProperty',
  AsHisSoleAndSeparateProperty = 'AsHisSoleAndSeparateProperty',
  AsJointTenants = 'AsJointTenants',
  AsTenantsInCommon = 'AsTenantsInCommon',
  AsTenancyByEntirety = 'AsTenancyByEntirety',
  EachAsToAnUndividedOneHalfInterest = 'EachAsToAnUndividedOneHalfInterest',
  EachAsToAnUndividedOneThirdInterest = 'EachAsToAnUndividedOneThirdInterest',
  EachAsToAnUndividedOneFourthInterest = 'EachAsToAnUndividedOneFourthInterest',
  HusbandAndWife = 'HusbandAndWife',
  HusbandAndWifeAsJointTenants = 'HusbandAndWifeAsJointTenants',
  HusbandAndWifeAsJointTenantsWithRightOfSurvivorship = 'HusbandAndWifeAsJointTenantsWithRightOfSurvivorship',
  HusbandAndWifeAsTenantsInCommon = 'HusbandAndWifeAsTenantsInCommon',
  AsJointTenantsWithRightOfSurvivorship = 'AsJointTenantsWithRightOfSurvivorship',
  WifeAndHusband = 'WifeAndHusband',
  BothUnmarried = 'BothUnmarried',
  SpousesMarriedToEachOther = 'SpousesMarriedToEachOther',
  CommunityProperty = 'CommunityProperty',
  JointTenants = 'JointTenants',
  SingleMan = 'SingleMan',
  SingleWoman = 'SingleWoman',
  MarriedMan = 'MarriedMan',
  MarriedWoman = 'MarriedWoman',
  TenantsInCommon = 'TenantsInCommon',
  TenancyByEntirety = 'TenancyByEntirety',
  ToBeDecidedInEscrow = 'ToBeDecidedInEscrow',
  UnmarriedMan = 'UnmarriedMan',
  UnmarriedWoman = 'UnmarriedWoman',
  Other = 'Other',
}

export const MANNER_TITLE_HELD_AS_OPTIONS: ReadonlyArray<{ value: CdMannerTitleHeldAs; label: string }> = [
  { value: CdMannerTitleHeldAs.SoleOwnership, label: 'Sole Ownership' },
  { value: CdMannerTitleHeldAs.LifeEstate, label: 'Life Estate' },
  { value: CdMannerTitleHeldAs.TenancyInCommon, label: 'Tenancy in Common' },
  { value: CdMannerTitleHeldAs.JointTenancyWithRightOfSurvivorship, label: 'Joint Tenancy with Right of Survivorship' },
  { value: CdMannerTitleHeldAs.TenancyByTheEntirety, label: 'Tenancy by the Entirety' },
  { value: CdMannerTitleHeldAs.AllAsJointTenants, label: 'All as Joint Tenants' },
  { value: CdMannerTitleHeldAs.AllAsTenantsInCommon, label: 'All as Tenants in Common' },
  { value: CdMannerTitleHeldAs.AsCommunityProperty, label: 'As Community Property' },
  { value: CdMannerTitleHeldAs.AsHerSoleAndSeparateProperty, label: 'As Her Sole And Separate Property' },
  { value: CdMannerTitleHeldAs.AsHisSoleAndSeparateProperty, label: 'As His Sole And Separate Property' },
  { value: CdMannerTitleHeldAs.AsJointTenants, label: 'As Joint Tenants' },
  { value: CdMannerTitleHeldAs.AsTenantsInCommon, label: 'As Tenants in Common' },
  { value: CdMannerTitleHeldAs.AsTenancyByEntirety, label: 'As Tenancy by Entirety' },
  { value: CdMannerTitleHeldAs.EachAsToAnUndividedOneHalfInterest, label: 'Each as to an Undivided One Half Interest' },
  { value: CdMannerTitleHeldAs.EachAsToAnUndividedOneThirdInterest, label: 'Each as to An Undivided One Third Interest' },
  { value: CdMannerTitleHeldAs.EachAsToAnUndividedOneFourthInterest, label: 'Each as to An Undivided One Fourth Interest' },
  { value: CdMannerTitleHeldAs.HusbandAndWife, label: 'Husband And Wife' },
  { value: CdMannerTitleHeldAs.HusbandAndWifeAsJointTenants, label: 'Husband And Wife as Joint Tenants' },
  { value: CdMannerTitleHeldAs.HusbandAndWifeAsJointTenantsWithRightOfSurvivorship, label: 'Husband And Wife as Joint Tenants With Right of Survivorship' },
  { value: CdMannerTitleHeldAs.HusbandAndWifeAsTenantsInCommon, label: 'Husband And Wife as Tenants in Common' },
  { value: CdMannerTitleHeldAs.AsJointTenantsWithRightOfSurvivorship, label: 'As Joint Tenants With Right of Survivorship' },
  { value: CdMannerTitleHeldAs.WifeAndHusband, label: 'Wife And Husband' },
  { value: CdMannerTitleHeldAs.BothUnmarried, label: 'Both Unmarried' },
  { value: CdMannerTitleHeldAs.SpousesMarriedToEachOther, label: 'Spouses Married to Each Other' },
  { value: CdMannerTitleHeldAs.CommunityProperty, label: 'Community Property' },
  { value: CdMannerTitleHeldAs.JointTenants, label: 'Joint Tenants' },
  { value: CdMannerTitleHeldAs.SingleMan, label: 'Single Man' },
  { value: CdMannerTitleHeldAs.SingleWoman, label: 'Single Woman' },
  { value: CdMannerTitleHeldAs.MarriedMan, label: 'Married Man' },
  { value: CdMannerTitleHeldAs.MarriedWoman, label: 'Married Woman' },
  { value: CdMannerTitleHeldAs.TenantsInCommon, label: 'Tenants in Common' },
  { value: CdMannerTitleHeldAs.TenancyByEntirety, label: 'Tenancy by Entirety' },
  { value: CdMannerTitleHeldAs.ToBeDecidedInEscrow, label: 'To be Decided in Escrow' },
  { value: CdMannerTitleHeldAs.UnmarriedMan, label: 'Unmarried Man' },
  { value: CdMannerTitleHeldAs.UnmarriedWoman, label: 'Unmarried Woman' },
  { value: CdMannerTitleHeldAs.Other, label: 'Other' },
];

/**
 * Amortization types for CD Request
 * Maps to: CdAmortizationType enum
 */
export enum CdAmortizationType {
  AdjustableRate = 'AdjustableRate',
  Fixed = 'Fixed',
  GraduatedPaymentMortgage = 'GraduatedPaymentMortgage',
  OtherAmortizationType = 'OtherAmortizationType',
}

/**
 * Buydown contributor types for CD Request
 * Maps to: CdBuydownContributorType enum
 */
export enum CdBuydownContributorType {
  Borrower = 'Borrower',
  Builder = 'Builder',
  Employer = 'Employer',
  Lender = 'Lender',
  NonParentRelative = 'NonParentRelative',
  Parent = 'Parent',
  Seller = 'Seller',
  Unassigned = 'Unassigned',
  UnrelatedFriend = 'UnrelatedFriend',
}

/**
 * Flow type for Correspondent channel CD/ICD requests
 * Maps to: ClosingDisclosureFlowType enum
 */
export enum ClosingDisclosureFlowType {
  /** Initial Closing Disclosure - hides MERS MIN and Warehouse fields */
  Icd = 'Icd',
  /** Document Order (Final Closing Documents) - shows all fields including MERS MIN and Warehouse */
  DocOrder = 'DocOrder',
}
